/**
 * testApi.js
 * ----------
 * Live backend client API for Education > Tests module.
 * Fully compliant with Rule 90 (Zero Mock Data) and Rule 89 (Background Jobs).
 */
import api from '../../../../api/client'

export async function listTests(params = {}) {
  const { data } = await api.get('/education/tests', { params })
  return data
}

export async function getTestDetail(id) {
  const { data } = await api.get(`/education/tests/${id}`)
  return data
}

export async function createTest(payload) {
  const { data } = await api.post('/education/tests', payload)
  return data
}

export async function updateTest(id, payload) {
  const { data } = await api.put(`/education/tests/${id}`, payload)
  return data
}

export async function deleteTest(id) {
  const { data } = await api.delete(`/education/tests/${id}`)
  return data
}

export async function fetchMarksRoster(testId, populateMarks = 'A', orderBy = 0) {
  const { data } = await api.get(`/education/tests/${testId}/marks-roster`, {
    params: { populate_marks: populateMarks, order_by: orderBy },
  })
  return data
}

export async function saveMarks(testId, marksList) {
  const { data } = await api.post(`/education/tests/${testId}/marks`, {
    test_id: testId,
    marks: marksList,
  })
  return data
}

export async function deleteMarks(testId) {
  const { data } = await api.delete(`/education/tests/${testId}/marks`)
  return data
}

export async function fetchTestStatistics(testId) {
  const { data } = await api.get(`/education/tests/${testId}/statistics`)
  return data
}

export async function requestTestsExport(filters = {}) {
  const { data } = await api.post('/background-jobs/', {
    job_type: 'EXPORT',
    module: 'tests',
    payload: filters,
  })
  return data
}

export async function requestTestMarksExport(testId, populateMarks = 'A', orderBy = 0) {
  const { data } = await api.post('/background-jobs/', {
    job_type: 'EXPORT',
    module: 'test_marks',
    payload: { test_id: testId, populate_marks: populateMarks, order_by: orderBy },
  })
  return data
}

// ── Question Paper Client APIs ──────────────────────────────────────────────

export async function fetchQuestionPaper(testId) {
  const { data } = await api.get(`/education/tests/${testId}/question-paper`)
  return data
}

export async function saveQuestionPaper(testId, payload) {
  const { data } = await api.post(`/education/tests/${testId}/question-paper`, payload)
  return data
}

export async function uploadQuestionPaperFile(testId, file) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post(`/education/tests/${testId}/question-paper/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteQuestionPaperFile(testId) {
  const { data } = await api.delete(`/education/tests/${testId}/question-paper/file`)
  return data
}

// ── Solution Client APIs ────────────────────────────────────────────────────

export async function fetchSolution(testId) {
  const { data } = await api.get(`/education/tests/${testId}/solution`)
  return data
}

export async function uploadSolution(testId, file) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post(`/education/tests/${testId}/solution/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteSolution(testId) {
  const { data } = await api.delete(`/education/tests/${testId}/solution`)
  return data
}

export const DATE_PERIOD_OPTIONS = [
  { value: 'period', label: 'Period' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'current_week', label: 'Current Week' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'current_year', label: 'Current Year' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'next_week', label: 'Next Week' },
  { value: 'next_month', label: 'Next Month' },
  { value: 'next_year', label: 'Next Year' },
]

export async function fetchReportCardHtml(testId, studentId = null) {
  const params = studentId ? { studentId } : {}
  const { data } = await api.get(`/education/tests/${testId}/report-card/html`, { params })
  return data
}

export async function dispatchTestNotifications(testId, payload) {
  const { data } = await api.post(`/education/tests/${testId}/notifications`, payload)
  return data
}
